import { z } from "zod";

// Schema for plan limits - FleetONE: apenas veículos
export const LimitsSchema = z.object({
  // === FLEET ONE (apenas veículos) ===
  fleet_vehicles_max: z.number().int().nonnegative().optional(),
  
  // Genéricos (backwards compatibility)
  vehicles_max: z.number().int().nonnegative().optional(),
});

// Schema for plan feature toggles (mantido vazio para FleetONE)
export const TogglesSchema = z.object({});

// Complete features schema
export const FeaturesSchema = z.object({
  limits: LimitsSchema.default({}),
  toggles: TogglesSchema.default({}),
});

export type Features = z.infer<typeof FeaturesSchema>;
export type Limits = z.infer<typeof LimitsSchema>;
export type Toggles = z.infer<typeof TogglesSchema>;

// Plan data structure
export const PlanSchema = z.object({
  id: z.string().uuid(),
  system_id: z.string().uuid(),
  name: z.string(),
  description: z.string().optional(),
  price_monthly: z.number(),
  price_yearly: z.number(),
  features: FeaturesSchema,
  is_active: z.boolean(),
  sort_order: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type Plan = z.infer<typeof PlanSchema>;

// Company system plan
export const CompanySystemPlanSchema = z.object({
  id: z.string().uuid(),
  company_id: z.string().uuid(),
  system_id: z.string().uuid(),
  plan_id: z.string().uuid(),
  status: z.enum(['active', 'pending', 'suspended', 'canceled']),
  start_date: z.string(),
  end_date: z.string().optional(),
  billing_period: z.enum(['monthly', 'yearly']),
  external_subscription_id: z.string().optional(),
  metadata: z.record(z.any()).default({}),
  created_at: z.string(),
  updated_at: z.string(),
});

export type CompanySystemPlan = z.infer<typeof CompanySystemPlanSchema>;

// Plan audit entry
export const PlanAuditSchema = z.object({
  id: z.string().uuid(),
  company_id: z.string().uuid(),
  system_id: z.string().uuid(),
  from_plan_id: z.string().uuid().optional(),
  to_plan_id: z.string().uuid().optional(),
  action: z.enum(['upgrade', 'downgrade', 'cancel', 'activate', 'suspend']),
  reason: z.string().optional(),
  actor_user_id: z.string().uuid().optional(),
  created_at: z.string(),
});

export type PlanAudit = z.infer<typeof PlanAuditSchema>;

// Usage counter
export const UsageCounterSchema = z.object({
  id: z.string().uuid(),
  company_id: z.string().uuid(),
  system_id: z.string().uuid(),
  metric: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  value: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type UsageCounter = z.infer<typeof UsageCounterSchema>;

// System information
export const SystemSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().optional(),
  app_base_path: z.string().optional(),
  default_features: FeaturesSchema.optional(),
  created_at: z.string(),
});

export type System = z.infer<typeof SystemSchema>;

// Check context for plan validation
export type CheckContext = {
  companyId: string;
  systemCode: "FLEET" | "FISCAL" | "CONTRACTS";
};

// Plan check types
export type ToggleCheck = {
  type: "toggle";
  key: keyof Toggles;
};

export type LimitCheck = {
  type: "limit";
  key: keyof Limits;
  intent?: string;
  delta?: number;
};

export type PlanCheck = ToggleCheck | LimitCheck;

// Plan validation result
export type PlanValidationResult = {
  allowed: boolean;
  reason?: string;
  cap?: number;
  used?: number;
};